'use client';

import React, { useEffect, useState } from 'react';
import { Page } from '@/types/page';

const SidebarLayout: React.FC<{ page: Page; children: React.ReactNode; heading: string }> = ({
  page,
  children,
  heading,
}) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Helper to generate section IDs including nested service blocks
  const getSectionIds = (page: Page): string[] => {
    const ids: string[] = [];

    page.blocks?.forEach((b: any) => {
      if (!b.show_this_section_in_the_sidebar) return;

      const baseId =
        'heading' in b && typeof b.heading === 'string'
          ? b.heading.toLowerCase().replace(/\s+/g, '-')
          : b.__typename;

      if (baseId === 'ComponentSectionsOurServices' && Array.isArray(b.services)) {
        b.services.forEach((_: any, idx: number) => {
          ids.push(`${baseId}-service-${idx}`);
        });
      } else {
        ids.push(baseId);
      }
    });

    return ids;
  };

  useEffect(() => {
    if (!page?.blocks) return;

    const sectionIds = getSectionIds(page);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            break;
          }
        }
      },
      {
        rootMargin: '0px 0px -60% 0px',
        threshold: 0.3,
      }
    );

    const elements: HTMLElement[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        elements.push(el);
      }
    });

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [page]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 100 && page.blocks?.length) {
        const topBlock = page.blocks.find((b: any) => b.show_this_section_in_the_sidebar);
        if (topBlock) {
          const id =
            'heading' in topBlock && typeof topBlock.heading === 'string'
              ? topBlock.heading.toLowerCase().replace(/\s+/g, '-')
              : topBlock.__typename;
          setActiveSection(id);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [page]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const toRoman = (num: number): string => {
    const romans: [string, number][] = [
      ['m', 1000],
      ['cm', 900],
      ['d', 500],
      ['cd', 400],
      ['c', 100],
      ['xc', 90],
      ['l', 50],
      ['xl', 40],
      ['x', 10],
      ['ix', 9],
      ['v', 5],
      ['iv', 4],
      ['i', 1],
    ];
    let result = '';
    for (const [letter, value] of romans) {
      while (num >= value) {
        result += letter;
        num -= value;
      }
    }
    return result;
  };

  return (
    <main className="min-h-screen">
      <div className="flex flex-col md:flex-row relative border-b border-[#B6C38066] middle-area">
        {/* Sidebar */}
        <div className="border-r sidebar-border-dynamic mt-5 pe-8 md:min-w-[300px] hidden xl:flex sidebar">
          <aside className="ps-5 min-h-screen relative mt-[16rem]">
            <nav className="space-y-4 sticky top-[20px]">
              <ul className="text-base font-light list-none ml-0 flex flex-col gap-2 about-leftlist">
                {page.blocks
                  ?.filter((b: any) => b.show_this_section_in_the_sidebar)
                  .flatMap((b: any, index: number) => {
                    const baseId =
                      'heading' in b && typeof b.heading === 'string'
                        ? b.heading.toLowerCase().replace(/\s+/g, '-')
                        : b.__typename;

                    if (baseId === 'ComponentSectionsOurServices' && Array.isArray(b.services)) {
                      return b.services.map((service: any, idx: number) => {
                        const serviceId = `${baseId}-service-${idx}`;
                        const isActive = activeSection === serviceId;
                        return (
                          <li
                            key={`${index}-${idx}`}
                            className={`text-left w-full leading-none cursor-pointer flex items-start ${
                              isActive ? 'sidebarActive font-medium' : ''
                            }`}
                          >
                            <span className="w-6 flex-shrink-0">{toRoman(idx + 1)}.</span>
                            <button
                              onClick={() => scrollToSection(serviceId)}
                              className="text-left font-light"
                            >
                              {service.heading}
                            </button>
                          </li>
                        );
                      });
                    }

                    const isActive = activeSection === baseId;
                    return (
                      <li
                        key={index}
                        className={`text-left w-full leading-none cursor-pointer flex items-start ${
                          isActive ? 'sidebarActive' : ''
                        }`}
                      >
                        <span className="w-6 flex-shrink-0">{toRoman(index + 1)}.</span>
                        <button
                          onClick={() => scrollToSection(baseId)}
                          className="text-left font-light"
                        >
                          {'heading' in b ? b.heading : b.__typename}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </nav>
          </aside>
        </div>

        {/* Main Content */}
        <div className="px-4 pt-20 md:p-5 w-full lg:w-fit">
          <div className="text-start md:text-center leading-none md:pt-1 md:ml-[4rem]">
            <h1 className="font-light">{heading}</h1>
          </div>
          <div className="main-content-sections">{children}</div>
        </div>
      </div>
    </main>
  );
};

export default SidebarLayout;
